import React, { useContext, useEffect, useState } from 'react';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { AudioRecordingContext, audioRecordingPortalName } from './AudioRecordingContainer';
import { Portal } from '@gorhom/portal';
import { RecordingPresets, requestRecordingPermissionsAsync, setAudioModeAsync, useAudioRecorder } from 'expo-audio';
import type { PermissionResponse } from 'expo-audio';
import Text from '../../v2BaseComponents/Text';
import { getIntl } from '../../../utils/intl';
import IconButton from '../../v2BaseComponents/IconButton';
import { CreatedRecording, useProcessAndUploadFile } from './useProcessAndUploadFile';
import { Loader } from '../../v2BaseComponents/Loader';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import getBrowser from '../../../utils/getBrowser';
import Alert from '../../v2BaseComponents/Alert';
import * as Linking from 'expo-linking';
import { recordingAVisitLearnMore, recordingDisabledHelpUrl } from '../../../utils/constants';
import { useLocationPartyConsentStatus } from '../../../hooks/useLocation';
import SkeletonLoadingContainer from '../../v2BaseComponents/SkeletonLoadingContainer';
import MaybeStackedButtons from '../MaybeStackedButtons';

const intl = getIntl('audio-recording-recorder', {
    title: 'New Recording',
    'start-recording': 'Push To Start Recording',
    'recording-inprogress': 'Recording in Progress',
    'recording-paused': 'Recording Paused',
    add: 'Add to Note',
    save: 'Save Recording',
    disabled: 'Please grant permission for audio recording!',
    saveRecording: 'Save Recording',
    uploadInProgress: 'Processing and uploading recording.  Do not go back or close the app.',
    sixtyMinLimit: 'Recording is limited to 30 minutes',
    fiveMinWarning: 'Recording is limited to 30 minutes. Less than 5 minutes remaining.',
    tipsForRecording: 'Tips for recording',
    tipsForRecordingMessage: 'It is a good idea to ask for permission and give your reason for recording.',
    singlePartyTipsForRecordingMessage:
        'In {state}, you are not required to request permission to record your healthcare provider but it is good practice to ask and give the reason for your recording.',
    allPartyTipsForRecordingMessage:
        'In {state}, you are required to request permission to record your healthcare provider.',
    learnMore: 'Learn more...',
    cancel: 'Cancel',
    permissionDisabledByBrowser: 'Audio recording is disabled in your browser.',
    permissionDisabledByBrowserSubtext:
        'You have disabled audio recordings previously.  You cannot create recordings until you re-enable the microphone in your devices settings.  Please review the help article on how to do this.  Refresh the page once this is done.',
});

const recordingLimit = 3599;

export type RecorderOwnProps = {
    onRecordingCreated: (recording: CreatedRecording) => void;
};

export type RecorderProps = RecorderOwnProps;

const formatTime = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;
    const maybePadWithLeadingZero = (num: number) => (num < 10 ? '0' : '') + num;
    return hours > 0
        ? `${hours}:${maybePadWithLeadingZero(minutes)}:${maybePadWithLeadingZero(seconds)}`
        : `${minutes}:${maybePadWithLeadingZero(seconds)}`;
};

const MaybeMountedRecorder: React.FC<RecorderProps & { onClose: () => void }> = (props) => {
    const partyConsentStatus = useLocationPartyConsentStatus();
    const browser = getBrowser(navigator.userAgent);
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [elapsedTime, setElapsedTime] = useState<number>(0);
    const [permissionResponse, setPermissionResponse] = useState<PermissionResponse | null>(null);
    const recordingOptions = {
        ...RecordingPresets.HIGH_QUALITY,
        web: {
            ...RecordingPresets.HIGH_QUALITY.web,
            mimeType: browser === 'Safari' ? 'audio/mp4' : RecordingPresets.HIGH_QUALITY.web?.mimeType,
        },
    };
    const recorder = useAudioRecorder(recordingOptions);
    const { isProcessingAndUploading, processAudio, killAudio } = useProcessAndUploadFile({
        onCreatedRecording: (createdRecording) => {
            props.onRecordingCreated(createdRecording);
            props.onClose();
        },
    });

    const requestPermission = async () => {
        const response = await requestRecordingPermissionsAsync();
        setPermissionResponse(response);
        return response;
    };

    useEffect(() => {
        requestPermission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAudioDisabledByBrowser =
        permissionResponse && permissionResponse.status === 'denied' && permissionResponse.expires === 'never';

    const startRecording = async () => {
        try {
            if (permissionResponse?.status !== 'granted') {
                try {
                    const response = await requestPermission();
                    if (response.status !== 'granted') {
                        console.error('Permission not granted');
                        return;
                    }
                } catch (err) {
                    console.error('Failed to request permission', err);
                    return;
                }
            }

            await setAudioModeAsync({
                allowsRecording: true,
                playsInSilentMode: true,
            });

            await recorder.prepareToRecordAsync(recordingOptions);
            recorder.record();
            setIsRecording((prev) => !prev);
        } catch (err) {
            console.error('Failed to start recording', err);
        }
    };

    const pauseRecording = async () => {
        recorder.pause();
    };

    const unpauseRecording = async () => {
        recorder.record();
    };

    const stopRecording = async () => {
        setIsRecording(false);
        processAudio(recorder);
    };

    // acquire wake lock to keep the screen on and always kill the audio when the component unmounts
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null;
        const acquireWakeLock = async (): Promise<WakeLockSentinel | null> => {
            const result = await navigator?.wakeLock?.request?.('screen');
            return result || null;
        };

        acquireWakeLock()
            .then((result) => {
                wakeLock = result;
            })
            .catch((err) => {
                console.error('Failed to acquire wake lock', err);
            });

        return () => {
            // useAudioRecorder owns the recorder lifecycle and releases the native
            // shared object on unmount. Calling stop() here races with that release
            // and throws "Unable to find the native shared object". The Cancel button
            // path explicitly stops the recorder before unmounting; this branch only
            // needs to release the wake lock.
            setIsRecording(false);
            if (wakeLock) {
                try {
                    wakeLock.release();
                } catch (err) {
                    console.error('Failed to release wake lock', err);
                }
            }
        };
    }, []);

    useEffect(() => {
        if (elapsedTime > recordingLimit) {
            stopRecording();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (isRecording) {
            timer = setInterval(() => {
                setElapsedTime((prevTime) => prevTime + 1);
            }, 1000);
        } else if (!recorder.isRecording && elapsedTime !== 0) {
            clearInterval(timer!);
        }
        return () => {
            clearInterval(timer!);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRecording]);

    const renderBottomContent = () => {
        if (elapsedTime || isRecording) {
            return (
                <MaybeStackedButtons
                    forceStack
                    secondaryButtonProps={{
                        variant: 'secondary',
                        testID: 'audio-recording-cancel-button',
                        onPress: () => {
                            killAudio(recorder).finally(() => {
                                props.onClose();
                            });
                        },
                        title: intl('cancel'),
                        icon: 'XCircle',
                    }}
                    primaryButtonProps={{
                        variant: 'primary',
                        testID: 'audio-upload-recording-button',
                        isDisabled: isRecording,
                        onPress: stopRecording,
                        title: intl('saveRecording'),
                        icon: 'UploadCloud',
                    }}
                />
            );
        } else {
            let content = intl('tipsForRecordingMessage');
            if (partyConsentStatus.status === 'success') {
                content =
                    partyConsentStatus.consent === 'single'
                        ? intl('singlePartyTipsForRecordingMessage', { state: partyConsentStatus.state })
                        : intl('allPartyTipsForRecordingMessage', { state: partyConsentStatus.state });
            } else if (partyConsentStatus.status === 'loading') {
                return (
                    <SkeletonLoadingContainer isLoading={partyConsentStatus.status === 'loading'}>
                        <Alert
                            type="info"
                            title={intl('tipsForRecording')}
                            message={content}
                            actionItems={[
                                {
                                    title: intl('learnMore'),
                                    onPress: () => Linking.openURL(recordingAVisitLearnMore),
                                },
                            ]}
                        />
                    </SkeletonLoadingContainer>
                );
            } else {
                return null;
            }
        }
    };

    const renderContent = () => {
        if (isAudioDisabledByBrowser) {
            return (
                <Alert
                    type="error"
                    title={intl('permissionDisabledByBrowser')}
                    message={intl('permissionDisabledByBrowserSubtext')}
                    actionItems={[
                        {
                            title: intl('learnMore'),
                            onPress: () => Linking.openURL(recordingDisabledHelpUrl),
                        },
                    ]}
                />
            );
        } else if (isProcessingAndUploading) {
            return (
                <Stack
                    vertical
                    fullHeight
                    fullWidth
                    align="center"
                    justify="center"
                    space={contentStackSpacing}
                    testID="uploading-recording-loader"
                >
                    <Loader size="sm" />
                    <Text variant="caption-1" alignText="center">
                        {intl('uploadInProgress')}
                    </Text>
                </Stack>
            );
        } else {
            return (
                <>
                    <Text variant="h1" color="Gray-Text">
                        {formatTime(elapsedTime)}
                    </Text>
                    {permissionResponse?.status != 'granted' && (
                        <Text variant="bold-h4" alignText="center" color="Alert">
                            {intl('disabled')}
                        </Text>
                    )}
                    <Text variant="card-title" alignText="center">
                        {isRecording
                            ? intl('recording-inprogress')
                            : elapsedTime == 0
                            ? intl('start-recording')
                            : intl('recording-paused')}
                    </Text>
                    {isRecording && elapsedTime < 3600 && (
                        <Text variant="card-support" alignText="center">
                            {intl('sixtyMinLimit')}
                        </Text>
                    )}
                    {elapsedTime > recordingLimit - 300 && (
                        <Text variant="bold-h4" alignText="center" color="Alert">
                            {intl('fiveMinWarning')}
                        </Text>
                    )}
                    <IconButton
                        customSize="avatar-icon"
                        variant="large-record-button"
                        icon={isRecording ? 'Pause' : 'Mic'}
                        onPress={() => {
                            if (elapsedTime > recordingLimit) return;
                            if (elapsedTime === 0) {
                                startRecording();
                            } else if (isRecording) {
                                pauseRecording();
                                setIsRecording((prev) => !prev);
                            } else {
                                unpauseRecording();
                                setIsRecording((prev) => !prev);
                            }
                        }}
                        isDisabled={elapsedTime > recordingLimit || permissionResponse?.status !== 'granted'}
                        testID="audio-start-stop-recording"
                    />
                    <Stack vertical style={{ minHeight: 42 }} fullWidth justify="center" align="center">
                        {renderBottomContent()}
                    </Stack>
                </>
            );
        }
    };

    return (
        <Stack vertical fullHeight fullWidth align="center" justify="center" space={contentStackSpacing}>
            {renderContent()}
        </Stack>
    );
};

const Recorder: React.FC<RecorderProps> = (props) => {
    const { isRecorderOpen, closeRecorder } = useContext(AudioRecordingContext);

    return (
        <Portal hostName={audioRecordingPortalName}>
            <MainContentContainer style={{ width: '100%', height: '100%' }}>
                <Stack vertical align="center" justify="center" fullWidth fullHeight>
                    {isRecorderOpen ? <MaybeMountedRecorder {...props} onClose={closeRecorder} /> : null}
                </Stack>
            </MainContentContainer>
        </Portal>
    );
};

export default React.memo(Recorder);
